import React from "react";
import PropTypes from "prop-types";
import "./BillCard.css";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Typography from "@mui/joy/Typography";
import Chip from "@mui/joy/Chip";
import { Stack } from "@mui/joy";
import { format } from "date-fns";
import avatar from "../../Male_Avatar.jpg";

function formatDate(dateString) {
  return format(new Date(dateString), "MMM d, yyyy");
}

function getPartyBorderColor(bill) {
  const raw = bill.sponsor?.party || bill.party || "";
  if (raw === "D" || raw === "Democratic") return "#0075b2";
  if (raw === "R" || raw === "Republican") return "#c1121f";
  return "#8899a8";
}

const BillCard = ({ bill, isStateBill = false }) => {
  const borderColor = getPartyBorderColor(bill);

  const goToDetails = (event) => {
    event.preventDefault();
    if (isStateBill) {
      const url = new URL("/state-bill-details", window.location.origin);
      url.searchParams.set("billId", bill.billId);
      url.searchParams.set("state", bill.state);
      window.history.pushState({}, "", url.toString());
    } else {
      const url = new URL("/details", window.location.origin);
      url.searchParams.set("congress", bill.congress);
      url.searchParams.set("type", bill.type);
      url.searchParams.set("billIdNumber", bill.billIdNumber || bill.number);
      window.history.pushState({}, "", url.toString());
    }
    const navEvent = new PopStateEvent("popstate");
    window.dispatchEvent(navEvent);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToDetails(event);
    }
  };

  return (
    <Card
      variant="plain"
      sx={{
        minWidth: "220px",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        backgroundColor: "white",
        border: "none",
        borderTop: `8px solid ${borderColor}`,
        boxShadow: "0px 1px 1.5px rgba(166,175,195,0.4)",
        cursor: "pointer",
        borderRadius: "4px",
        padding: 0,
        transition: "box-shadow 0.15s ease",
        "&:hover": {
          boxShadow: "0px 4px 16px rgba(166,175,195,0.65)",
        },
        "&:focus-visible": {
          outline: "2px solid var(--ce-accent)",
          outlineOffset: "2px",
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
        },
      }}
      component="div"
      role="button"
      tabIndex={0}
      onClick={goToDetails}
      onKeyDown={handleKeyDown}
      aria-label={`View details for ${bill.title || bill.fullTitle}`}
    >
      <CardContent
        sx={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          gap: 0,
        }}
      >
        <Stack spacing={2} sx={{ flexGrow: 1 }}>
          {bill.category && (
            <Typography
              sx={{
                color: "#637381",
                fontWeight: 400,
                fontSize: "14px",
                textTransform: "uppercase",
                lineHeight: "22px",
              }}
            >
              {bill.category}
            </Typography>
          )}

          <Typography
            sx={{
              color: "#181d27",
              fontSize: "24px",
              fontWeight: 600,
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {bill.title || bill.fullTitle}
          </Typography>

          {bill.tags?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {bill.tags.slice(0, 3).map((tag, index) => (
                <Chip
                  key={index}
                  variant="soft"
                  size="sm"
                  sx={{
                    backgroundColor: "#e5e7eb",
                    color: "#374151",
                    fontSize: "12px",
                    fontWeight: 500,
                    borderRadius: "24px",
                    "--Chip-paddingInline": "8px",
                    "--Chip-minHeight": "20px",
                  }}
                >
                  {tag}
                </Chip>
              ))}
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{
            mt: "32px",
            pt: "24px",
            borderTop: "1px solid #dfe4ea",
          }}
        >
          <img
            src={bill.sponsor?.imageUrl || bill.authorImage || avatar}
            alt={bill.sponsor?.name ? `${bill.sponsor.name} portrait` : "Sponsor portrait"}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
          <Stack spacing={0} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: "#181d27",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: 1.42,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {bill.sponsor?.name || bill.sponsor || "Unknown"}
            </Typography>
            {bill.updateDate && (
              <Typography
                sx={{
                  color: "#535862",
                  fontSize: "14px",
                  lineHeight: 1.42,
                }}
              >
                {formatDate(bill.updateDate)}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

BillCard.propTypes = {
  bill: PropTypes.object.isRequired,
};

export default BillCard;
